// HR / Školení — interní školení + testy pro tým (10.8.2026, zadání Ládi).
//
// Seznam školení → rozklik na detail s výsledky. Owner/manager přiřadí test
// libovolným lidem z týmu (hr-list-people), každý dostane vlastní odkaz
// /skoleni/<token> (kopíruje se tlačítkem, posílá se ručně např. Slackem).
// Detail: tabulka pokusů (stav, skóre, čas), rozklik na odpovědi po otázkách.
import React, { useMemo, useState } from "react";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { getRealUser, getCurrentUser } from "../../lib/auth.js";
import { confirmDialog } from "../../components/confirmHost.jsx";
import { Modal, HlavickaOkna, KartaSeznam, Stitek } from "../../components/ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { Users, UserPlus, Clock, Link2, Trash2, ChevronRight, ChevronDown, Loader2, ExternalLink, Search, Copy, Check, Play, FileText, ArrowUpRight, List } from "lucide-react";
import SkoleniMaterialy, { PrepinacDlazdice } from "./SkoleniMaterialy.jsx";

const fmtDatum = (d) => (d ? new Date(d).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" }) : "");
const fmtCas = (sec) => (sec == null ? "" : `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`);

const STAV_BADGE = {
  prirazeno: { text: "Čeká na vyplnění", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  probiha: { text: "Probíhá", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  hotovo: { text: "Dokončeno", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  vyprselo: { text: "Čas vypršel", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  vyprselo_bez_odeslani: { text: "Čas vypršel, neodesláno", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

function ProcentBadge({ procent }) {
  if (procent == null) return <span className="text-slate-400">-</span>;
  const cls = procent >= 75 ? "text-emerald-700" : procent >= 50 ? "text-amber-700" : "text-rose-700";
  return <span className={`font-bold ${cls}`}>{procent} %</span>;
}

function CopyBtn({ text, label }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); } catch { /* ok */ }
      }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
      title={text}
    >
      {ok ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
      {ok ? "Zkopírováno" : (label || "Odkaz")}
    </button>
  );
}

// ── Modal: přiřazení lidem ────────────────────────────────────────────────
function AssignModal({ skoleniId, onClose, onDone }) {
  const { data } = useApiData("/api/hr-list-people?filter=vse&status=aktivni");
  const people = data?.people || [];
  const [vybrani, setVybrani] = useState(new Set());
  const [hledat, setHledat] = useState("");
  const [busy, setBusy] = useState(false);
  const [odkazy, setOdkazy] = useState(null); // [{person_name, url, novy}]

  const filtrovani = useMemo(() => {
    const q = hledat.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) => `${p.display_name} ${p.pozice || ""}`.toLowerCase().includes(q));
  }, [people, hledat]);

  const toggle = (id) => setVybrani((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  async function potvrdit() {
    setBusy(true);
    try {
      const lide = people.filter((p) => vybrani.has(p.id)).map((p) => ({ id: p.id, jmeno: p.display_name }));
      const r = await apiFetch(`/api/hr-skoleni?id=${encodeURIComponent(skoleniId)}&action=assign`, {
        method: "POST", body: JSON.stringify({ lide }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) { await confirmDialog({ title: "Přiřazení selhalo", description: j.error || "Zkus to prosím znovu.", confirmLabel: "Rozumím", cancelLabel: "Zavřít", variant: "warning" }); return; }
      setOdkazy((j.prirazeno || []).map((x) => ({ ...x, url: `${window.location.origin}/skoleni/${x.token}` })));
      onDone && onDone();
    } catch (e) { await confirmDialog({ title: "Přiřazení selhalo", description: "Zkus to prosím znovu.", confirmLabel: "Rozumím", cancelLabel: "Zavřít", variant: "warning" }); }
    finally { setBusy(false); }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={odkazy ? Link2 : UserPlus} nazev={odkazy ? "Odkazy na test" : "Přiřadit test lidem"} onClose={onClose} />}
      footer={!odkazy ? (
        <button onClick={potvrdit} disabled={busy || vybrani.size === 0} className="btn btn-primary w-full">
          {busy ? "Přiřazuji…" : `Přiřadit (${vybrani.size})`}
        </button>
      ) : undefined}
    >
        {odkazy ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500 mb-3">
              Pošli každému jeho odkaz (např. Slackem). Odkaz je osobní, test jde vyplnit jen jednou
              a čas se spouští až kliknutím na Spustit test.
            </p>
            {odkazy.map((o, i) => (
              <div key={i} className="flex items-center justify-between gap-2 p-2.5 bg-slate-50 rounded-xl border border-slate-200">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate demo-sensitive">{o.person_name}</div>
                  {!o.novy && <div className="text-[11px] text-amber-700">Už měl(a) přiřazeno, posílá se stejný odkaz</div>}
                </div>
                <CopyBtn text={o.url} />
              </div>
            ))}
            <button onClick={onClose} className="btn btn-primary mt-3 w-full">Hotovo</button>
          </div>
        ) : (
          <>
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={hledat} onChange={(e) => setHledat(e.target.value)} placeholder="Hledat člověka…"
                  className="field w-full pl-9 pr-3"
                />
              </div>
            </div>
            <div className="mt-3 space-y-1">
              {filtrovani.map((p) => (
                <label key={p.id} className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer ${vybrani.has(p.id) ? "border-brand-300 bg-brand-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <input type="checkbox" checked={vybrani.has(p.id)} onChange={() => toggle(p.id)} className="accent-brand-600" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate demo-sensitive">{p.display_name}</div>
                    {p.pozice && <div className="text-[11px] text-slate-500">{p.pozice}</div>}
                  </div>
                </label>
              ))}
              {filtrovani.length === 0 && <div className="text-sm text-slate-400 italic p-2">Nikdo nenalezen.</div>}
            </div>
          </>
        )}
    </Modal>
  );
}

// ── Rozklik odpovědí jednoho pokusu ───────────────────────────────────────
function PokusDetail({ skoleniId, pokusId }) {
  const { data, loading } = useApiData(`/api/hr-skoleni?id=${encodeURIComponent(skoleniId)}&pokus=${encodeURIComponent(pokusId)}`);
  if (loading) return <div className="p-4 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám odpovědi…</div>;
  const detail = data?.detail || [];
  return (
    <div className="p-4 bg-slate-50 border-t border-slate-200 space-y-2.5">
      {detail.map((d, i) => (
        <div key={i} className="text-sm">
          <div className="font-medium text-slate-700"><span className="text-slate-400 mr-1">{i + 1}.</span>{d.ot}</div>
          <div className={`mt-0.5 ml-4 ${d.ok ? "text-emerald-700" : "text-rose-700"}`}>
            {d.ok ? "✓" : "✗"} {d.odpoved != null ? d.moznosti[d.odpoved] : "(bez odpovědi)"}
            {!d.ok && <span className="text-slate-500"> (správně: {d.moznosti[d.spravna]})</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Detail školení s výsledky ─────────────────────────────────────────────
function SkoleniDetail({ id, role, onBack }) {
  const { data, loading, refetch } = useApiData(`/api/hr-skoleni?id=${encodeURIComponent(id)}`);
  const [assignOpen, setAssignOpen] = useState(false);
  const [rozklik, setRozklik] = useState(null);
  const s = data?.skoleni;
  const pokusy = data?.pokusy || [];
  const hotove = pokusy.filter((p) => p.finished_at);
  const prumer = hotove.length ? Math.round(hotove.reduce((a, p) => a + (p.procent || 0), 0) / hotove.length) : null;

  async function smazatPokus(pokusId) {
    const ano = await confirmDialog({
      title: "Smazat pokus",
      description: "Smaže se přiřazení i případný výsledek testu. Tohle nejde vrátit.",
      confirmLabel: "Smazat",
      variant: "danger",
    });
    if (!ano) return;
    const r = await apiFetch(`/api/hr-skoleni?id=${encodeURIComponent(id)}&action=smazat-pokus`, {
      method: "POST", body: JSON.stringify({ pokusId }),
    });
    const j = await r.json();
    if (!r.ok || !j.ok) { await confirmDialog({ title: "Smazání selhalo", description: j.error || "Zkus to prosím znovu.", confirmLabel: "Rozumím", cancelLabel: "Zavřít", variant: "warning" }); return; }
    refetch();
  }

  if (loading && !s) return <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;
  if (!s) return null;

  return (
    <div className="space-y-4">
      <button onClick={onBack}
        className="btn btn-primary">
        Zpět na seznam školení
      </button>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-bold text-slate-800">{s.nazev}</div>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">{s.popis}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
              <span>{s.pocet_otazek} otázek</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> limit {s.limit_min} min</span>
              {s.notion_url && (
                <a href={s.notion_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-brand-700 hover:underline">
                  <ExternalLink className="w-3.5 h-3.5" /> Poznámky ke školení
                </a>
              )}
            </div>
          </div>
          <button
            onClick={() => setAssignOpen(true)}
            className="btn btn-primary"
          >
            <Users className="w-4 h-4" /> Přiřadit lidem
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-black text-slate-800">{pokusy.length}</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wide">Přiřazeno</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-black text-emerald-700">{hotove.length}</div>
            <div className="text-[11px] text-emerald-700/70 uppercase tracking-wide">Dokončeno</div>
          </div>
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 text-center">
            <div className="whitespace-nowrap text-xl font-black text-brand-700 sm:text-2xl">{prumer == null ? "-" : `${prumer} %`}</div>
            <div className="text-[11px] text-brand-700/70 uppercase tracking-wide">Průměrná úspěšnost</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 font-bold text-slate-700 text-sm">Výsledky</div>
        {pokusy.length === 0 && (
          <div className="p-6 text-sm text-slate-400 italic">Zatím nikdo nemá test přiřazený. Začni tlačítkem Přiřadit lidem.</div>
        )}
        {pokusy.map((p) => {
          const b = STAV_BADGE[p.stav] || STAV_BADGE.prirazeno;
          const otevreny = rozklik === p.id;
          return (
            <div key={p.id} className="border-b border-slate-100 last:border-b-0">
              <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => p.finished_at && setRozklik(otevreny ? null : p.id)}
                  className={`flex items-center gap-2 min-w-0 w-full sm:w-auto sm:flex-1 text-left ${p.finished_at ? "cursor-pointer" : "cursor-default"}`}
                >
                  {p.finished_at
                    ? (otevreny ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />)
                    : <span className="w-4 shrink-0" />}
                  <span className="text-sm font-semibold text-slate-800 truncate demo-sensitive">{p.person_name}</span>
                  <span className={`shrink-0 whitespace-nowrap px-2 py-0.5 rounded-full border text-[11px] font-semibold ${b.cls}`}>{b.text}</span>
                </button>
                {/* 25. 10. 2026 (Michal): na telefonu se štítek stavu překrýval s výsledkem,
                    výsledek proto na úzké obrazovce jde na vlastní řádek pod jméno. */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-6 text-sm sm:pl-0">
                  {p.finished_at ? (
                    <>
                      <span className="text-slate-700 font-semibold">{p.spravne}/{p.celkem}</span>
                      <ProcentBadge procent={p.procent} />
                      <span className="text-slate-500 text-xs flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{fmtCas(p.trvani_sec)}</span>
                      <span className="text-slate-400 text-xs">{fmtDatum(p.finished_at)}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-slate-400 text-xs">přiřazeno {fmtDatum(p.prirazeno_at)}</span>
                      <CopyBtn text={`${window.location.origin}/skoleni/${p.token}`} />
                    </>
                  )}
                  {role === "owner" && (
                    <button onClick={() => smazatPokus(p.id)} title="Smazat pokus" className="text-slate-300 hover:text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {otevreny && <PokusDetail skoleniId={id} pokusId={p.id} />}
            </div>
          );
        })}
      </div>

      {assignOpen && <AssignModal skoleniId={id} onClose={() => setAssignOpen(false)} onDone={refetch} />}
    </div>
  );
}


// ── Seznam testů podle školení (16. 10. 2026, schválený mockup) ──────────
// Testy ke školením (okruhy Marketing, Manuál trenéra, Zákaznická péče) jsou seskupené,
// ostatní samostatné testy (smlouvy, prodej, core) jsou ve skupině Ostatní testy.
const MRIZKA_TESTY = "grid grid-cols-[minmax(0,1fr)_120px_100px_90px_44px] items-center gap-3";

function SeznamTestu({ skoleni, loading, onVybrat }) {
  const uzka = useUzkaObrazovka();
  const skupiny = useMemo(() => {
    const m = new Map();
    for (const s of skoleni) { const k = s.okruh || "Ostatní testy"; if (!m.has(k)) m.set(k, []); m.get(k).push(s); }
    const arr = [...m.entries()];
    arr.sort((a, b) => (a[0] === "Ostatní testy") - (b[0] === "Ostatní testy"));
    return arr;
  }, [skoleni]);
  const [otevrene, setOtevrene] = useState(null);
  const aktivni = otevrene ?? new Set(skupiny.length ? [skupiny[0][0]] : []);
  const prepni = (k) => { const n = new Set(aktivni); if (n.has(k)) n.delete(k); else n.add(k); setOtevrene(n); };
  if (loading && !skoleni.length) return <div className="flex items-center gap-2 p-4 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Načítám testy…</div>;
  return (
    <KartaSeznam ikona={List} title="Seznam testů" odznak={skoleni.length}>
      <div className="p-3">
        {skupiny.map(([okruh, polozky]) => {
          const otevreno = aktivni.has(okruh);
          return (
            <div key={okruh} className="mt-2 first:mt-0">
              <button type="button" onClick={() => prepni(okruh)} aria-expanded={otevreno}
                className={`flex min-h-[52px] w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left ${otevreno ? "bg-brand-light text-brand-deep" : "border border-slate-200 bg-white text-slate-900"}`}>
                {otevreno ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                <span className="flex-1 text-[15px] font-bold">{okruh}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${otevreno ? "bg-white" : "bg-brand-light text-brand-deep"}`}>{polozky.length}</span>
              </button>
              {otevreno && (
                <div className="mt-1">
                  {!uzka && (
                    <div className={`${MRIZKA_TESTY} px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500`}>
                      <span>Test</span><span>Přiřazeno</span><span>Hotovo</span><span>Průměr</span><span />
                    </div>
                  )}
                  {polozky.map((s) => {
                    const lide = `${s.prirazeno} ${s.prirazeno === 1 ? "člověk" : s.prirazeno >= 2 && s.prirazeno <= 4 ? "lidé" : "lidí"}`;
                    const tl = <button type="button" onClick={() => onVybrat(s.id)} className="btn-ikona" aria-label={`Otevřít test ${s.nazev}`} title="Otevřít"><ArrowUpRight className="h-5 w-5" /></button>;
                    return uzka ? (
                      <div key={s.id} className="polozka mt-2 flex cursor-pointer items-center gap-3" onClick={() => onVybrat(s.id)}>
                        <div className="min-w-0 flex-1">
                          <div className="text-base font-bold leading-snug text-slate-900">{s.nazev}</div>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <Stitek>{lide}</Stitek><Stitek>{s.hotovo} hotovo</Stitek>
                            {s.prumer != null && <Stitek ton="ok">{s.prumer} %</Stitek>}
                          </div>
                        </div>
                        {tl}
                      </div>
                    ) : (
                      <div key={s.id} className={`${MRIZKA_TESTY} cursor-pointer border-t border-slate-100 px-3 py-3 transition hover:bg-brand-50/60`} onClick={() => onVybrat(s.id)}>
                        <div className="min-w-0">
                          <div className="truncate text-[15px] font-bold text-slate-900" title={s.nazev}>{s.nazev}</div>
                          <div className="text-[13px] text-slate-500">{s.pocet_otazek} otázek, {s.limit_min} min</div>
                        </div>
                        <span className="text-sm text-slate-600">{lide}</span>
                        <span className="text-sm text-slate-600">{s.hotovo} z {s.prirazeno}</span>
                        <span>{s.prumer != null ? <Stitek ton="ok">{s.prumer} %</Stitek> : <span className="text-slate-400">-</span>}</span>
                        {tl}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </KartaSeznam>
  );
}

// ── Hlavní stránka ────────────────────────────────────────────────────────
export default function HRSkoleni() {
  const { data, loading } = useApiData("/api/hr-skoleni");
  const [vybrane, setVybrane] = useState(null);
  const [sekce, setSekce] = useState("skoleni");
  const role = String((getRealUser() || {}).role || "").toLowerCase();
  // Nahledova role (owner si prepne "Nahled: Manager"): backend vraci testy podle
  // REALNE role, tady se owner-only testy schovaji, aby nahled ukazoval to,
  // co manager opravdu uvidi (21.8.2026).
  const nahledRole = String((getCurrentUser() || {}).role || "").toLowerCase();
  const skoleni = (data?.skoleni || []).filter((s) => nahledRole === "owner" || String(s.viditelnost || "manager") !== "owner");

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <GradientHeader
        variant="slate"
        title="Školení"
        subtitle="Školení: videa, prezentace a dokumenty, každé přidělíš konkrétním lidem. Testy: každý dostane osobní odkaz s časovým limitem, výsledky vidíš tady."
      />

      {/* 15. 10. 2026 (schválený mockup): Školení (videa, prezentace, dokumenty s přidělením) a Testy. */}
      <PrepinacDlazdice hodnota={sekce} onChange={(k) => { setSekce(k); setVybrane(null); }} polozky={[
        { key: "skoleni", nazev: "Školení", ikona: Play, pod: "videa, prezentace a dokumenty" },
        { key: "testy", nazev: "Testy", ikona: FileText, pod: skoleni.length ? `${skoleni.length} ${skoleni.length === 1 ? "test" : skoleni.length < 5 ? "testy" : "testů"}, výsledky týmu` : "výsledky týmu" },
      ]} />

      {sekce === "skoleni" ? (
        <SkoleniMaterialy />
      ) : vybrane ? (
        <SkoleniDetail id={vybrane} role={role} onBack={() => setVybrane(null)} />
      ) : (
        <SeznamTestu skoleni={skoleni} loading={loading} onVybrat={setVybrane} />
      )}
    </div>
  );
}
